import {
  ConsolidatedDataConfig,
  InputDependentDropdownDataApiResponse,
  ItemTransactionDetail,
  TlnReportsInitialState,
  TransactionDetailApiResponse
} from "../interface";
import { MockData } from "../__test__/MockData";
import stringToNumber from "src/utils/stringToNumber";
import {
  API_STATUS,
  API_STATUS_TYPE,
  TRANSACTION_QUALIFIER
} from "src/config/constants/genericConstants";
import { formatNumber } from "src/utils/genericFunctions";
import { formatDependentDropdownData } from "../Utils";
import { getCustomDifferenceDate, getFormattedDate } from "src/utils/getDates";
import { PayloadAction, SerializedError } from "@reduxjs/toolkit";

const [fromDate, toDate] = getCustomDifferenceDate({
  startDateDifferenceDay: 6
});
export const initialState: TlnReportsInitialState = {
  filters: {
    fromDate: getFormattedDate(fromDate),
    toDate: getFormattedDate(toDate),
    storeId: "",
    registers: [],
    cashiers: [],
    transactionId: "",
    upcOrItemId: "",
    transactionTotal: true,
    lineItemAmount: "",
    tenderTypes: [],
    lineItemType: [],
    totalAmountMin: "",
    totalAmountMax: "",
    errorMessage: ""
  },
  queryResult: {
    status: API_STATUS.IDLE as API_STATUS_TYPE,
    data: [],
    error: ""
  },
  itemTypes: {
    status: API_STATUS.IDLE as API_STATUS_TYPE,
    data: MockData.itemTypes,
    error: ""
  },
  masterDropdowns: {
    status: API_STATUS.IDLE as API_STATUS_TYPE,
    data: [],
    error: ""
  },
  transationDetail: {
    status: API_STATUS.IDLE as API_STATUS_TYPE,
    data: {
      tableData: {
        sales: [],
        refunds: []
      },
      consolidatedData: []
    },
    error: ""
  }
};

export const handleResetState = () => {
  return initialState;
};

export const handleResetReportFilter = (state: TlnReportsInitialState) => {
  state.filters = initialState.filters;
};

export const handleResetReportFilterResult = (
  state: TlnReportsInitialState
) => {
  state.queryResult = initialState.queryResult;
};

export const handlePendingForFetchTransactionLogsByQuery = (
  state: TlnReportsInitialState
) => {
  const updatedData = { ...initialState.queryResult };
  updatedData.status = API_STATUS.PENDING as API_STATUS_TYPE;
  state.queryResult = updatedData;
};

export const handleFullFillForFetchTransactionLogsByQuery = (
  state: TlnReportsInitialState,
  action: TransactionDetailApiResponse
) => {
  const updatedData = { ...initialState.queryResult };
  updatedData.status = API_STATUS.IDLE as API_STATUS_TYPE;
  updatedData.data = action.payload?.data.map(
    (eachData: ItemTransactionDetail, index: number) => ({
      ...eachData,
      id: index,
      totalAmount: formatNumber(eachData.totalAmount)
    })
  );
  state.queryResult = updatedData;
};

export const handleRejectForFetchTransactionLogsByQuery = (
  state: TlnReportsInitialState,
  action: PayloadAction<unknown, string, any, SerializedError>
) => {
  if (action?.error?.message !== API_STATUS.ERROR_CANCELED) {
    const updatedData = { ...initialState.queryResult };
    updatedData.status = API_STATUS.ERROR as API_STATUS_TYPE;
    state.queryResult = updatedData;
  }
};

export const handlePendingForFetchDependentDropdowns = (
  state: TlnReportsInitialState
) => {
  const updatedData = { ...initialState.masterDropdowns };
  updatedData.status = API_STATUS.PENDING as API_STATUS_TYPE;
  state.masterDropdowns = updatedData;
};

export const handleFullFillForFetchDependentDropdowns = (
  state: TlnReportsInitialState,
  action: InputDependentDropdownDataApiResponse
) => {
  const updatedData = { ...initialState.masterDropdowns };
  updatedData.status = API_STATUS.IDLE as API_STATUS_TYPE;
  updatedData.data = formatDependentDropdownData(action.payload.data);
  state.masterDropdowns = updatedData;
};

export const handleRejectForFetchDependentDropdowns = (
  state: TlnReportsInitialState
) => {
  const updatedData = { ...initialState.masterDropdowns };
  updatedData.status = API_STATUS.ERROR as API_STATUS_TYPE;
  state.masterDropdowns = updatedData;
};

export const sampleTransactionDetail = {
  date: "",
  transactionId: "",
  itemId: "",
  upc: "",
  quantity: "",
  department: "",
  taxable: "",
  taxAmount: "",
  recordQualifier: "",
  tenderAmount: "",
  tenderTypeCode: "",
  description: "",
  price: "",
  item: "",
  transactionSeq: "",
  id: ""
};

export const getConsolidatedDataConfig = () =>
  ({
    primary: {
      subTotal: {
        value: 0,
        label: "SUB TOTAL"
      },
      tax: {
        value: 0,
        label: "TAX"
      }
    },
    secondary: {
      totalPaid: {
        value: 0,
        label: "TOTAL PAID"
      }
    },
    tenderTypes: {},
    discounts: {
      couponsAndDiscount: {
        value: 0,
        label: "COUPONS AND DISCOUNT"
      }
    }
  } as ConsolidatedDataConfig);

export const filterQualifiers = (data: ItemTransactionDetail[]) =>
  data.reduce(
    (result: { [key: string]: any }, eachItem: ItemTransactionDetail) => {
      if (result?.[eachItem.recordQualifier]) {
        result?.[eachItem.recordQualifier].push(eachItem);
      }
      return result;
    },
    {
      [TRANSACTION_QUALIFIER.NORMAL_SALES_ITEM]: [],
      [TRANSACTION_QUALIFIER.TAX_INFO]: [],
      [TRANSACTION_QUALIFIER.TENDER_INFO]: [],
      [TRANSACTION_QUALIFIER.GIFT_COUPANS]: []
    }
  );

const getUpdatedNormalSaleItemConsolidatedData = (
  data: ItemTransactionDetail[]
) => {
  const updateSequence = (
    eachData: ItemTransactionDetail,
    result: { [key: string]: any }
  ) => {
    result.subTotal += stringToNumber(eachData.price);
    if (stringToNumber(eachData.price) >= 0) {
      result.salesSeq += 1;
      eachData.transactionSeq = result.salesSeq;
      result.sales.push(eachData);
    } else {
      result.refundSeq += 1;
      eachData.transactionSeq = result.refundSeq;
      result.refunds.push(eachData);
    }
  };
  return data.reduce(
    (
      result: { [key: string]: any },
      eachTransaction: ItemTransactionDetail
    ) => {
      result.index += 1;
      const rowData = {
        ...eachTransaction,
        price: formatNumber(eachTransaction.price),
        item: eachTransaction.description,
        id: `${result.index}`
      };
      updateSequence(rowData, result);
      return result;
    },
    {
      sales: [],
      refunds: [],
      index: 0,
      salesSeq: 0,
      refundSeq: 0,
      subTotal: 0
    }
  );
};

const getConsolidatedTax = (data: ItemTransactionDetail[]) =>
  data.reduce((store: number, current: { taxAmount: number | string }) => {
    return store + stringToNumber(current.taxAmount);
  }, 0);

const getConsolidatedDiscount = (data: ItemTransactionDetail[]) =>
  data.reduce((store: number, current: ItemTransactionDetail) => {
    return store + stringToNumber(current.reductionAmount);
  }, 0);

const getConsolidatedTenderInfoData = (data: ItemTransactionDetail[]) => {
  const tenderTypes: {
    [key: string]: {
      value: number;
      label: string;
    };
  } = {};
  let totaltender = 0;
  for (const eachData of data) {
    tenderTypes[eachData.tenderTypeCode] ||= {
      value: 0,
      label: eachData.tenderTypeCode
    };

    const tenderAmount = stringToNumber(eachData.tenderAmount);
    totaltender += tenderAmount;
    tenderTypes[eachData.tenderTypeCode].value += tenderAmount;
  }
  return { tenderTypes, totaltender };
};
export const getTransactionItemTotalValues = (
  data: ItemTransactionDetail[]
) => {
  const consolidatedData: any = [];
  const { primary, secondary, discounts } = getConsolidatedDataConfig();
  const qualifiersData = filterQualifiers(data);
  const { subTotal, sales, refunds } = getUpdatedNormalSaleItemConsolidatedData(
    qualifiersData?.[TRANSACTION_QUALIFIER.NORMAL_SALES_ITEM]
  );
  const tax = getConsolidatedTax(
    qualifiersData?.[TRANSACTION_QUALIFIER.TAX_INFO]
  );
  const discountAmount = getConsolidatedDiscount(
    qualifiersData?.[TRANSACTION_QUALIFIER.GIFT_COUPANS]
  );
  const { tenderTypes, totaltender } = getConsolidatedTenderInfoData(
    qualifiersData?.[TRANSACTION_QUALIFIER.TENDER_INFO]
  );
  const appendConsolidatedData = (
    cosolidatedData: {
      [key: string]: { [key: string]: string | number };
    },
    type: string
  ) => {
    for (const [index, eachKey] of Object.keys(cosolidatedData).entries()) {
      consolidatedData.push({
        ...sampleTransactionDetail,
        transactionSeq: "",
        id: `${type}_${index}`,
        isTlogReportConsolidatedRow: true,
        description: cosolidatedData?.[eachKey]?.label as string,
        price: formatNumber(cosolidatedData?.[eachKey]?.value || "")
      });
    }
  };
  primary.subTotal.value = subTotal;
  primary.tax.value = tax;
  secondary.totalPaid.value = totaltender;
  discounts.couponsAndDiscount.value = discountAmount;
  if (sales?.length > 0 || refunds?.length > 0) {
    appendConsolidatedData(primary, "primaryConsolidatedData");
    appendConsolidatedData(discounts, "discountConsolidatedData");
    appendConsolidatedData(tenderTypes, "tenderTypeConsolidatedData");
    appendConsolidatedData(secondary, "secondaryConsolidatedData");
  }
  return { tableData: { sales, refunds }, consolidatedData };
};

export const handlePendingForFetchTransactionDetail = (
  state: TlnReportsInitialState
) => {
  const updatedData = { ...initialState.transationDetail };
  updatedData.status = API_STATUS.PENDING as API_STATUS_TYPE;
  state.transationDetail = updatedData;
};

export const handleFullFillForFetchTransactionDetail = (
  state: TlnReportsInitialState,
  action: TransactionDetailApiResponse
) => {
  const updatedData = { ...initialState.transationDetail };
  updatedData.status = API_STATUS.IDLE as API_STATUS_TYPE;
  updatedData.data = getTransactionItemTotalValues(action.payload.data);
  state.transationDetail = updatedData;
};

export const handleRejectForFetchTransactionDetail = (
  state: TlnReportsInitialState
) => {
  const updatedData = { ...initialState.transationDetail };
  updatedData.status = API_STATUS.ERROR as API_STATUS_TYPE;
  state.transationDetail = updatedData;
};
